import { useCallback, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import {
  getValidFeatureScreen,
  setFeatureScreenCount,
} from '@sportworld/js-sdk/src/actions/featureScreen'
import { isDevMode } from '@sportworld/js-sdk/src/envs/envs'
import { getUrlParam } from '@sportworld/js-sdk/src/utils/utils'

import { openContentFromPreviewTile } from '@/actions'
import { appOpenedSource, eventCategory } from '@/constants/analytics'
import { paths } from '@/constants/paths'
import { useOpenTile } from '@/hooks/useOpenTile'
import {
  getModelCode,
  getModelName,
  getModelYear,
  getVersion,
  platformManufacturer,
} from '@/platform/platform'
import { platformName } from '@/platform/platformUtils'
import useStore from '@/store/useStore'
import { DeepLinkDataType } from '@/types/servicesTypes/flowTypes'
import { getMainHomePath } from '@/utils/route'
import { tizenDeepLinkHandler, sendEventToGTAG } from '@/utils/utils'

// import { sendAnalyticsEvent } from '@/utils/analyticsUtils'

export function useOpenAppController() {
  const navigate = useNavigate()
  const featureScreens = useStore(
    (state) => state.common.appConfig.featureScreens,
  )
  const deepLinkSwitcher = useDeepLinkSwitcher()

  useEffect(() => {
    const path = window.location.pathname
    const isSmartPreviewPage = path.includes(paths.SETTINGS_SMART_PREVIEW)
    !isSmartPreviewPage && appController()
  }, [])

  useEffect(() => {
    window.addEventListener('appcontrol', appController)
    return () => {
      window.removeEventListener('appcontrol', appController)
    }
  })

  const sendAnalyticsEvent = (data?: DeepLinkDataType) => {
    const mode = isDevMode ? 'developer' : 'production'

    if (data?.dataFromPreviewTiles) {
      const name = data?.dataFromPreviewTiles?.title
      const category = data?.dataFromPreviewTiles?.data?.event?.categoryId
      sendEventToGTAG(eventCategory.APP_OPENED, {
        incoming_source: appOpenedSource.SMART_HUB,
        name,
        category,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      })
      /* sendAnalyticsEvent({
        object: eventObject.APP,
        action: eventAction.OPENED,
        incoming_source: appOpenedSource.SMART_HUB,
        app_version: import.meta.env.REACT_APP_VERSION,
        smartHub: {
          name,
          category,
        },
        mode,
      }) */
    } else if (data?.dataFromUniversalGuide) {
      sendEventToGTAG(eventCategory.APP_OPENED, {
        incoming_source: appOpenedSource.UNIVERSAL_GUIDE,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      })
      /* sendAnalyticsEvent({
        object: eventObject.APP,
        action: eventAction.OPENED,
        incoming_source: appOpenedSource.UNIVERSAL_GUIDE,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      }) */
    } else if (data?.dataFromPromotion) {
      sendEventToGTAG(eventCategory.APP_OPENED, {
        incoming_source: appOpenedSource.DEEP_LINK,
        name: data?.dataFromPromotion?.referrer,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      })
      /* sendAnalyticsEvent({
        object: eventObject.APP,
        action: eventAction.OPENED,
        incoming_source: appOpenedSource.DEEP_LINK,
        promotionName: data?.dataFromPromotion?.referrer,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      }) */
    } else {
      sendEventToGTAG(eventCategory.APP_OPENED, {
        incoming_source: appOpenedSource.NORMAL,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      })
      /* sendAnalyticsEvent({
        object: eventObject.APP,
        action: eventAction.OPENED,
        incoming_source: appOpenedSource.NORMAL,
        app_version: import.meta.env.REACT_APP_VERSION,
        mode,
      }) */
    }
    sendEventToGTAG(eventCategory.PLATFORM, {
      manufacture: platformManufacturer,
      model: getModelName(),
      model_code: getModelCode(),
      model_year: getModelYear(),
      os_name: platformName,
      os_version: getVersion(),
    })
  }

  const deepLinkHandler = (data?: DeepLinkDataType) => {
    sendAnalyticsEvent(data)
    const pathname = window.location.pathname
    const search = window.location.search
    const path = `${pathname}${search}`
    const featureScreen = getValidFeatureScreen(featureScreens)
    if (featureScreen) {
      setFeatureScreenCount(featureScreen.id)
      const deepLinkData = { data, path }
      return navigate(paths.FEATURE_SCREEN, {
        state: { deepLinkData, featureScreen },
      })
    }

    deepLinkSwitcher({ data, path })
  }

  const appController = () => {
    tizenDeepLinkHandler().then((data?: DeepLinkDataType) => {
      deepLinkHandler(data)
    })
  }

  return deepLinkHandler
}

export function useDeepLinkSwitcher() {
  const navigate = useNavigate()
  const { setPromotionData } = useStore((state) => state.promotion)
  const openEvent = useOpenTile()

  return useCallback(
    ({ data, path }: { data?: DeepLinkDataType; path?: string }) => {
      if (data?.dataFromPreviewTiles) {
        openContentFromPreviewTile(
          data.dataFromPreviewTiles,
          navigate,
          openEvent,
        )
      } else if (data?.dataFromUniversalGuide) {
        navigate(`/event/${data.dataFromUniversalGuide}`)
      } else if (data?.dataFromPromotion) {
        setPromotionData(data?.dataFromPromotion)
        navigate(paths.LOGIN)
      } else {
        const regionId = getUrlParam('regionId')
        let pathname = path || window.location.pathname
        if (pathname === paths.ROOT || pathname === paths.INDEX) {
          pathname = getMainHomePath()
        }
        regionId
          ? navigate(`${pathname.split('?')[0]}?regionId=${regionId}`)
          : navigate(pathname)
      }
    },
    [navigate, setPromotionData, openEvent],
  )
}
